import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnDestroy,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ProcessService } from '@features/process-scheduler/process.service';
import { ProcessTaskStatus } from '@features/process-scheduler/process/process.model';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ChatMediaPreviewComponent } from '@features/process-scheduler/chat/chat-media-preview/chat-media-preview.component';
import { Subscription } from 'rxjs';
import { CommonService } from '@app/services/common.service';
import { AuditorTask } from '@features/process-scheduler/auditor/auditor.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AuthenticationService } from '@app/services/auth.service';

export interface ObservationData {
  id?: string;
  observationNotes?: string;
  attachments?: {
    id?: string;
    originalFileName?: string;
    dataURI?: string;
    url?: string;
    downloadUrl?: string;
    fileType?: string;
    mimeType?: string;
    createdAt?: string;
    type?: string;
  }[];
  issueFound: boolean;
  remark: string;
  status?: number;
  process?: any;
  processName?: string;
  groupId?: string;
  createdAt?: string;
  parentMessage?: any;
  startDateTime?: string;
  endDateTime?: string;
  isCritical?: boolean;

  eventMetadata: {
    ipdId: string;
    ipdNumber: string;
    roomNo: string;
    dischargeDate: string;
    dischargeTime: string;
    patientName: string;
  };
}

@Component({
  selector: 'app-process-observation',
  templateUrl: './process-observation.component.html',
  styleUrls: ['./process-observation.component.scss'],
})
export class ProcessObservationComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Input() processTaskData: any | null = null;
  @Input() moduleName: string = '';
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() attachmentsChanged = new EventEmitter<void>();
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('detailFileInput') detailFileInput!: ElementRef<HTMLInputElement>;

  observationData: ObservationData;
  observationForm!: FormGroup;
  uploadedFiles: File[] = [];
  filePreviewUrls: {
    id?: string;
    name?: string;
    dataURI?: string;
    url?: string;
    downloadUrl?: string;
    type?: string;
    fileType?: string;
    mimeType?: string;
    file?: any;
    uploadType?: string;
    createdAt?: string;
  }[] = [];
  isDragging: boolean = false;
  isUploadingAttachments = false;
  maxFileSize = 5 * 1024 * 1024; // 5MB
  allowedExtensions = [
    'png',
    'jpg',
    'jpeg',
    'pdf',
    'doc',
    'docx',
    'csv',
    'svg',
  ];

  taskId: string = '';
  isFormView: boolean = true;
  isDetailView: boolean = false;
  private issueFoundSubscription?: Subscription;
  previousRemarks: any[] = [];

  constructor(
    private fb: FormBuilder,
    private processService: ProcessService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private commonService: CommonService,
    private snackBar: MatSnackBar,
    private authService: AuthenticationService
  ) {}

  ngOnInit(): void {
    this.taskId = this.processTaskData?.id;
    this.initializeForm();
    this.setupIssueFoundSubscription();
    // console.log('processTaskData =>', this.processTaskData);
    this.loadTaskDetail(this.processTaskData?.id);
    if (
      this.processTaskData?.status === ProcessTaskStatus.MarkedAsDone ||
      (this.moduleName === 'audit' &&
        this.processTaskData?.status === ProcessTaskStatus.AuditFailed)
    ) {
      this.isFormView = false;
      this.isDetailView = true;
    } else if (this.processTaskData?.status == ProcessTaskStatus.AuditFailed) {
      // this.assignValueToForm();
      this.isFormView = true;
      this.isDetailView = false;
    }
    this.loadPreviousRemarks(this.processTaskData);
  }

  loadPreviousRemarks(taskDetail: AuditorTask): void {
    if (taskDetail?.statusHistory) {
      this.previousRemarks = taskDetail?.statusHistory.filter((item: any) => {
        return item.status === ProcessTaskStatus.AuditFailed;
      });
    }
  }

  assignValueToForm(): void {
    this.observationForm.patchValue({
      observationNotes: this.observationData?.observationNotes,
      issueFound: this.observationData?.issueFound,
      remark: this.observationData?.remark,
    });
    console.log('observationData =>', this.observationData);

    if (this.observationData?.attachments)
      this.assignValueToPreview(this.observationData?.attachments);
    // console.log('processTaskData=>', this.processTaskData);
  }

  assignValueToPreview(attachments: any[]): void {
    this.filePreviewUrls = [];
    attachments.forEach((attachment) => {
      const attachmentId = this.getAttachmentId(attachment);
      let obj = {
        id: attachmentId,
        name: attachment?.originalFileName || 'Attachment',
        dataURI: attachment?.url,
        url: attachment?.url,
        downloadUrl: attachment?.downloadUrl,
        type: attachment?.type,
        fileType: this.getFileType(attachment?.mimeType) || '',
        mimeType: attachment?.mimeType,
        file: attachment?.url,
        uploadType: attachmentId ? 'api' : 'local',
        createdAt: attachment?.createdAt,
      };
      this.filePreviewUrls.push(obj);
    });
  }

  ngOnDestroy(): void {
    if (this.issueFoundSubscription) {
      this.issueFoundSubscription.unsubscribe();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // if (changes['processTaskData'] && this.processTaskData && this.observationForm) {
    //   this.populateForm();
    // }
  }

  initializeForm(): void {
    const isObservationNotesRequired =
      this.processTaskData?.isObservationNotesRequired === true;

    this.observationForm = this.fb.group({
      taskId: [this.taskId],
      observationNotes: [
        '',
        isObservationNotesRequired ? Validators.required : null,
      ],
      issueFound: [null, Validators.required],
      remark: [''],
    });
  }

  setupIssueFoundSubscription(): void {
    this.issueFoundSubscription = this.observationForm
      .get('issueFound')
      ?.valueChanges.subscribe((value) => {
        const remarkControl = this.observationForm.get('remark');
        if (value === true) {
          remarkControl?.setValidators(Validators.required);
        } else {
          remarkControl?.clearValidators();
        }
        remarkControl?.updateValueAndValidity();
      });
  }

  loadTaskDetail(taskId: string | null) {
    this.processService.getTaskById(taskId).subscribe({
      next: (response) => {
        this.observationData = response?.task;
        // if (
        //   this.observationData?.attachments &&
        //   (this.observationData?.status === ProcessTaskStatus.MarkedAsDone ||
        //     (this.observationData?.status === ProcessTaskStatus.AuditFailed &&
        //       this.moduleName == 'audit'))
        // ) {
        if (this.observationData?.attachments) {
          this.filePreviewUrls = this.observationData?.attachments.map(
            (attachment) => {
              const attachmentId = this.getAttachmentId(attachment);
              return {
                id: attachmentId,
                name: attachment?.originalFileName || '',
                dataURI: attachment?.url,
                url: attachment?.url,
                downloadUrl: attachment?.downloadUrl,
                type: attachment?.type,
                fileType: this.getFileType(attachment?.mimeType || '') || '',
                mimeType: attachment?.mimeType,
                file: attachment?.url,
                uploadType: attachmentId ? 'api' : 'local',
                createdAt: attachment?.createdAt,
              };
            }
          );
        }
        if (this.processTaskData?.status == ProcessTaskStatus.AuditFailed) {
          this.assignValueToForm();
        }
      },
      error: (error) => {
        console.error('Error loading process schedulers:', error);
      },
    });
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    const files = event.dataTransfer?.files;
    if (files) {
      this.handleFiles(files, this.isDetailView && this.canAddAttachments);
    }
  }

  onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }
  isValidFile(file: File): boolean {
    const extension = file.name.split('.').pop()?.toLowerCase();

    // accept all videos (matches accept="video/*")
    if (file.type.startsWith('video/')) {
      return true;
    }

    if (!extension || !this.allowedExtensions.includes(extension)) {
      return false;
    }

    return true;
  }

  handleFiles(fileList: FileList, uploadImmediately = false): void {
    const filesArray = Array.from(fileList);
    const newlyAdded: File[] = [];

    for (const file of filesArray) {
      if (!this.isValidFile(file)) {
        this.snackBar.open(
          `File ${file.name} is not a valid file type. Only PNG, JPG, JPEG, PDF, DOC, DOCX, CSV and video files are allowed.`,
          'Close',
          { duration: 5000 }
        );
        continue;
      }

      this.uploadedFiles.push(file);
      newlyAdded.push(file);
    }

    if (uploadImmediately) {
      newlyAdded.forEach((file) => {
        const idx = this.uploadedFiles.indexOf(file);
        if (idx > -1) {
          this.uploadedFiles.splice(idx, 1);
        }
      });
      this.uploadAttachmentsViaApi(newlyAdded);
      return;
    }

    this.generateFileBase64();
  }

  onDetailFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.handleFiles(input.files, true);
    }
    if (this.detailFileInput?.nativeElement) {
      this.detailFileInput.nativeElement.value = '';
    }
  }

  generateFileBase64(): void {
    const newFiles = this.uploadedFiles.filter(
      (file) =>
        !this.filePreviewUrls.some(
          (preview) => preview.uploadType === 'local' && preview.file === file
        )
    );

    newFiles.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        let obj = {
          id: Date.now().toString() + file.name,
          name: file.name,
          dataURI: e.target.result,
          url: e.target.result,
          type: file.type,
          fileType: this.getFileType(file?.type) || '',
          mimeType: file.type,
          file: file,
          uploadType: 'local',
          createdAt: new Date().toISOString(),
        };
        this.filePreviewUrls.push(obj);
      };
      reader.readAsDataURL(file);
    });
  }

  uploadAttachmentsViaApi(files: File[]): void {
    if (!files.length || !this.taskId) return;

    const formData = new FormData();
    files.forEach((file) => {
      formData.append('attachments', file);
    });

    this.isUploadingAttachments = true;
    this.processService.addTaskAttachments(this.taskId, formData).subscribe({
      next: () => {
        this.snackBar.open('Attachment(s) uploaded successfully', 'Close', {
          duration: 3000,
        });
        this.isUploadingAttachments = false;
        this.loadTaskDetail(this.taskId);
        this.attachmentsChanged.emit();
      },
      error: (err) => {
        console.error(err);
        this.isUploadingAttachments = false;
        this.snackBar.open(
          err?.error?.message || 'Failed to upload attachment(s)',
          'Close',
          { duration: 3000 }
        );
      },
    });
  }

  getAttachmentId(attachment: any): string | undefined {
    return attachment?.id || attachment?._id || attachment?.attachmentId;
  }

  downloadAttachment(file: {
    url?: string;
    dataURI?: string;
    downloadUrl?: string;
    name?: string;
    mimeType?: string;
    type?: string;
    file?: any;
  }): void {
    this.commonService.downloadAttachment(
      file.url || file.dataURI || file.file,
      file.name || 'Attachment',
      file.mimeType || file.type,
      file.downloadUrl
    );
  }

  getPayload(): any {
    const isFileUploadRequired =
      this.processTaskData?.isFileUploadRequired === true;

    if (isFileUploadRequired && this.filePreviewUrls.length === 0) {
      this.observationForm.markAllAsTouched();
      return null;
    }

    if (this.observationForm.valid) {
      let formDataPayload = new FormData();
      formDataPayload.append('id', this.taskId);
      formDataPayload.append(
        'observationNotes',
        this.observationForm.value.observationNotes
      );
      formDataPayload.append(
        'issueFound',
        this.observationForm.value.issueFound
      );
      formDataPayload.append('remark', this.observationForm.value.remark);
      let fileData = this.filePreviewUrls.filter(
        (file) => file.uploadType === 'local'
      );
      fileData.map((file) => {
        formDataPayload.append('attachments', file.file);
      });

      return formDataPayload;
    } else {
      Object.keys(this.observationForm.controls).forEach((key) => {
        this.observationForm.get(key)?.markAsTouched();
      });
      return null;
    }
  }

  getMediaType(mimeType: string): string {
    if (!mimeType) return '';
    if (mimeType.startsWith('image/')) return 'image';
    if (mimeType.startsWith('video/')) return 'video';
    return 'file';
  }

  getFileType(mime: string): string {
    if (!mime) return '';
    if (mime === 'application/pdf') return 'pdf';
    if (mime.startsWith('image/')) return 'image';
    if (mime.startsWith('video/')) return 'video';
    if (mime === 'text/csv') return 'csv';
    if (
      mime === 'application/msword' ||
      mime ===
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ) {
      return 'doc';
    }
    return 'file';
  }

  viewMedia(msg: any) {
    if (!this.filePreviewUrls) return;

    // Map all files for the previewer
    const allFiles = this.filePreviewUrls.map((m) => ({
      previewUrl: m.url || m.dataURI,
      downloadUrl: m.downloadUrl || m.url,
      type: this.getMediaType(m.mimeType || m.type || ''),
      fileType: this.getFileType(m.mimeType || m.type || ''),
      mimeType: m.mimeType || m.type || '',
      name: m.name || 'Attachment',
      id: m.id,
      file: m.file,
    }));

    // console.log(allFiles);

    const clickedIndex = allFiles.findIndex((m) => m.id === msg?.id);

    this.dialog.open(ChatMediaPreviewComponent, {
      data: {
        files: allFiles,
        startIndex: clickedIndex !== -1 ? clickedIndex : 0,
      },
      panelClass: 'full-screen-modal',
      maxWidth: '85vw',
      maxHeight: '85vh',
    });
  }

  sanitizeUrl(url: string | undefined): SafeUrl {
    if (!url) return '';
    return this.sanitizer.bypassSecurityTrustUrl(url);
  }

  get isTaskClosed(): boolean {
    if (!this.processTaskData?.startDateTime) {
      return false;
    }

    const startDateTime = new Date(this.processTaskData.startDateTime);
    const nowUtc = new Date();
    const istOffsetMs = (5 * 60 + 30) * 60 * 1000;
    const nowIst = new Date(nowUtc.getTime() + istOffsetMs);

    return nowIst.getTime() < startDateTime.getTime();
  }

  get isTaskOwner(): boolean {
    const userId = this.authService.getCurrentUser()?.id;
    const taskData: any = this.observationData || this.processTaskData;
    if (!userId || !taskData) {
      return false;
    }

    if (taskData.markedDoneBy?.id === userId) {
      return true;
    }

    const assignees = [
      ...(taskData.primaryAssignees || []),
      ...(taskData.primaryAssigneesArray || []),
      ...(taskData.secondaryAssignees || []),
      ...(taskData.secondaryAssigneesArray || []),
    ];

    return assignees.some(
      (assignee: { id?: string; userId?: string }) =>
        assignee?.id === userId || assignee?.userId === userId
    );
  }

  get canAddAttachments(): boolean {
    if (!this.isTaskOwner) {
      return false;
    }

    if (this.isDetailView) {
      return true;
    }

    return this.isFormView && !this.isTaskClosed;
  }

  getFileIcon(fileType: string | undefined): string {
    switch (fileType?.toLowerCase()) {
      case 'doc':
      case 'docx':
        return 'doc';
      case 'csv':
      case 'xls':
      case 'xlsx':
        return 'csv';
      case 'video':
        return 'video';
      case 'pdf':
        return 'pdf';
      default:
        return 'insert_drive_file';
    }
  }

  getTooltip(element: HTMLElement): string {
    if (element.scrollWidth > element.clientWidth) {
      return element.innerText || element.textContent || '';
    } else {
      return '';
    }
  }
}
